// @symbo-ls/workspace-files — the four canvas-node mutation ops that stay EAGER.
//
// createCanvasFolder / commitRenameNode / deleteCanvasNode / moveNodesIntoFolder
// are el.call'd by the workspace AI layer (functions/aiWorkspaceOps.js) as
// aiCreateFolder / aiRenameFile / aiDeleteFile / aiMoveFileToFolder — user
// actions that can fire on ANY route (the assistant is a chat alternative to the
// UI), long before /files is ever opened. So these four must be registered
// eagerly: the shell's sharedLibraries.js imports them directly from here, while
// the REST of files.functions (and every Files* component) defers with the
// package context via `lazySharedLibPage('/files')`.
//
// Their placement + selection-ring closure lives in ./canvasGrid.js so the SAME
// _pendingCells instance backs both these eager ops and the deferred upload/move
// handlers in index.js.

import {
  _claimCell,
  _releaseCell,
  _rowId,
  _syncRingsAfterRender,
  _folderAncestors,
  snapToGrid,
  _cellKey,
  gridPlace,
  _uniqueSiblingName,
  _claimName,
  _releaseName,
  _unpositionedSpots,
  _nodeSpot
} from './canvasGrid.js'
import { getActiveWorkspacePointer } from '@symbo-ls/workspace-shared/functions/activeWorkspacePointer.js'

// Tab-truth-first workspace scope for every `workspaceProject.fileCanvas`
// write below — the tab's own resolved workspace (`root.workspace.id`)
// first, the per-tab pointer as a boot fallback. Threaded EXPLICITLY as
// `payload.workspaceId` on every create/update/remove call rather than left
// to the SDK's ambient `_context.activeWorkspaceId` fallback (WorkspaceProject
// Service's `_fileCanvasWorkspaceId` reads payload.workspaceId FIRST) —
// FILES-RENAME-1 (tickets/sonnet.md): a rename with no explicit workspaceId
// depends entirely on that ambient context being both SET and correct for
// the workspace the tab is actually viewing; when it isn't (MWT off — see
// mwtFlag.js's "stays pinned … FOREVER" note — or a boot race before
// `bootShell`'s `sdk.updateContext` lands), the server's G1 workspace-pin
// guard 404s ("not found"), caught here and only console.error'd — a rename
// that silently reverts on Enter with no toast. Reproduced directly against
// the dev API: `PATCH /file-canvas/:id` with no `?workspaceId=` 404s
// `not_found` for a real, visible row; the identical request WITH the
// explicit param succeeds. Duplicated (not imported) from functions/index.js's
// own `_filesActiveWsId` — same reasoning subscribeFilesRealtime.js's
// `_activeWsIdForRealtime` documents: nodeOps.js is the EAGER slice loaded
// before index.js's DEFERRED slice ever runs, and importing from index.js
// here would cycle back through nodeOps.js's own re-export in that file.
const _filesActiveWsId = (root) => {
  if (root?.workspace?.id) return root.workspace.id
  try {
    return getActiveWorkspacePointer() || null
  } catch (_) {
    return null
  }
}

// ── create folder ───────────────────────────────────────────────────────────
export const createCanvasFolder = async function (name) {
  const root = this.state.root
  const parentId = root.filesOpenFolder || null
  const requested = (name && String(name).trim()) || 'New folder'
  // Disambiguate against real siblings + any name claimed by another
  // in-flight create in this same folder — "New folder", "New folder 2",
  // "New folder 3", … instead of stacking duplicates (STUDIO/FILES-MICRO-1).
  const folderName = _uniqueSiblingName(root, requested, parentId)
  const nameKey = _claimName(parentId, folderName)
  // Claim + reserve the cell so a second rapid create (or a racing upload)
  // can't resolve the same cell and stack on top of this folder.
  const place = _claimCell(root)
  const payload = {
    kind: 'folder',
    name: folderName,
    parent_id: parentId,
    x: place.x,
    y: place.y,
    scope: root.filesScope || 'shared',
    created_by: root.currentUserEmail || null,
    workspaceId: _filesActiveWsId(root)
  }
  try {
    const res = await this.sdk.execute('workspaceProject.fileCanvas', 'create', { payload })
    const id = _rowId(res)
    if (!id) return null
    root.update({
      fileNodes: [
        ...(root.fileNodes || []),
        {
          id,
          kind: 'folder',
          name: folderName,
          parentId: payload.parent_id,
          x: place.x,
          y: place.y,
          fileId: null,
          docId: null,
          url: null,
          mime: null,
          size: null,
          scope: payload.scope,
          createdBy: payload.created_by,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ],
      // Auto-select the just-created folder (per Nika) — it mounts with its
      // ring; _syncRingsAfterRender clears any prior selection's rings.
      filesSelectedIds: [id]
    })
    _syncRingsAfterRender(this, [id])
    return id
  } catch (err) {
    console.error('[Files] createCanvasFolder failed:', err?.message || err)
    return null
  } finally {
    // Released after the optimistic fileNodes update above, so occupancy/name
    // uniqueness never drops the reservation between the in-flight claim and
    // the real row landing in root.fileNodes.
    _releaseCell(place)
    _releaseName(nameKey)
  }
}

// ── rename ───────────────────────────────────────────────────────────────────
export const commitRenameNode = async function (id, name) {
  const root = this.state.root
  const sdk = this.sdk
  const trimmed = (name || '').trim()
  const cur = (root.fileNodes || []).find((n) => n.id === id)
  if (root.filesRenaming === id) root.update({ filesRenaming: null }, { preventFetch: true })
  if (!sdk || !cur || !trimmed) return false
  if (trimmed === cur.name) return true
  const prevName = cur.name
  root.update(
    {
      fileNodes: (root.fileNodes || []).map((n) => (n.id === id ? { ...n, name: trimmed } : n))
    },
    { preventFetch: true }
  )
  try {
    await sdk.execute('workspaceProject.fileCanvas', 'update', {
      id,
      payload: { name: trimmed, workspaceId: _filesActiveWsId(root) }
    })
    return true
  } catch (err) {
    console.error('[Files] rename failed:', err?.message || err)
    // Roll back the optimistic name so the UI + the AI's report stay honest.
    root.update(
      {
        fileNodes: (root.fileNodes || []).map((n) => (n.id === id ? { ...n, name: prevName } : n))
      },
      { preventFetch: true }
    )
    // A rename that reverts with no explanation reads as data loss — surface
    // it instead of leaving the user to notice the name silently snapped
    // back (FILES-RENAME-1, tickets/sonnet.md).
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'files.rename.failedTitle'),
      message: err?.message || this.call('polyglot', 'files.rename.failedMessage')
    })
    return false
  }
}

// ── delete ────────────────────────────────────────────────────────────────────
// Remove a desktop node (its file_canvas row + best-effort the storage blob).
// Virtual /documents doc-icons (doc:<id>) have no row — not deletable here.
export const deleteCanvasNode = async function (id) {
  const root = this.state.root
  const sdk = this.sdk
  if (!id || !sdk || String(id).indexOf('doc:') === 0) return false
  const node = (root.fileNodes || []).find((n) => n.id === id)
  try {
    await sdk.execute('workspaceProject.fileCanvas', 'remove', {
      id,
      workspaceId: _filesActiveWsId(root)
    })
    const pos = { ...(root._fileNodePos || {}) }
    delete pos[id]
    root.update(
      {
        fileNodes: (root.fileNodes || []).filter((n) => n.id !== id),
        _fileNodePos: pos,
        filesSelectedIds: (root.filesSelectedIds || []).filter((x) => x !== id)
      },
      { preventFetch: true }
    )
    if (node && node.fileId) {
      sdk
        .execute('workspaceProject.storage', 'remove', {
          bucket: 'workspace-files',
          path: node.fileId
        })
        .catch((err) => console.warn('[Files] storage remove failed:', err?.message || err))
    }
    return true
  } catch (err) {
    console.error('[Files] deleteCanvasNode failed:', err?.message || err)
    // A refused delete says so (2026-10-30, CRUD standard rule 5) — the
    // confirm closed and nothing happened. The desktop drops a node only after
    // the server took the delete, so the item is still where it was.
    const name = (node && node.name) || this.call('polyglot', 'files.delete.itemFallback')
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'files.delete.failedTitle'),
      message: this.call('polyglot', 'files.delete.failedMessage').replace('{name}', () => name)
    })
    return false
  }
}

// ── move into folder ──────────────────────────────────────────────────────────
// Re-parent dropped node(s) into a folder (or back to the Desktop root when
// folderId is null), each on a free cell inside the target. Virtual doc-icons
// materialise a real row with the target as parent.
export const moveNodesIntoFolder = function (ids, folderId) {
  const root = this.state.root
  const sdk = this.sdk
  if (!sdk || folderId === undefined || !ids || !ids.length) return
  const ancestors = _folderAncestors(root, folderId)
  const list = ids.filter((id) => id !== folderId && !ancestors.has(id))
  if (!list.length) return
  const pos = root._fileNodePos || {}
  // A position-less node in the target (a server-created row) counts at its
  // placed cell, so a moved-in node never lands on it.
  const spots = _unpositionedSpots(root)
  const occ = new Set()
  ;(root.fileNodes || []).forEach((n) => {
    if ((n.parentId || null) !== folderId) return
    const p = _nodeSpot(root, n, spots)
    const c = snapToGrid(p.x, p.y)
    occ.add(_cellKey(c.x, c.y))
  })
  let scan = 0
  const nextCell = () => {
    let p = gridPlace(scan++)
    while (occ.has(_cellKey(p.x, p.y)) && scan < 2000) p = gridPlace(scan++)
    occ.add(_cellKey(p.x, p.y))
    return p
  }
  const cellById = {}
  list.forEach((id) => {
    const cell = nextCell()
    if (String(id).indexOf('doc:') === 0) {
      const docId = String(id).slice(4)
      const doc = (root.fileDocs || []).find((d) => d.id === docId)
      const payload = {
        kind: 'doc',
        name: (doc && doc.title) || 'Document',
        parent_id: folderId,
        x: cell.x,
        y: cell.y,
        doc_id: docId,
        scope: root.filesScope || 'shared',
        created_by: root.currentUserEmail || null,
        workspaceId: _filesActiveWsId(root)
      }
      sdk
        .execute('workspaceProject.fileCanvas', 'create', { payload })
        .then((res) => {
          const realId = _rowId(res)
          if (!realId) return
          root.update(
            {
              fileNodes: [
                ...(root.fileNodes || []),
                {
                  id: realId,
                  kind: 'doc',
                  name: payload.name,
                  parentId: folderId,
                  x: cell.x,
                  y: cell.y,
                  fileId: null,
                  docId,
                  url: null,
                  mime: null,
                  size: null,
                  scope: payload.scope,
                  createdBy: payload.created_by
                }
              ]
            },
            { preventFetch: true }
          )
        })
        .catch((err) => console.error('[Files] move doc into folder failed:', err?.message || err))
      return
    }
    cellById[id] = cell
    sdk
      .execute('workspaceProject.fileCanvas', 'update', {
        id,
        payload: { parent_id: folderId, x: cell.x, y: cell.y, workspaceId: _filesActiveWsId(root) }
      })
      .catch((err) => console.error('[Files] move into folder failed:', err?.message || err))
  })
  const movedIds = new Set(Object.keys(cellById))
  const nextPos = { ...pos }
  movedIds.forEach((id) => {
    nextPos[id] = cellById[id]
  })
  root.update(
    {
      fileNodes: (root.fileNodes || []).map((n) =>
        movedIds.has(n.id)
          ? { ...n, parentId: folderId, x: cellById[n.id].x, y: cellById[n.id].y }
          : n
      ),
      _fileNodePos: nextPos,
      filesSelectedIds: []
    },
    { preventFetch: true }
  )
}
